/**
 * Das Raster der Belegung: Tageskopf und Apartmentzeilen in einem horizontal scrollbaren Bereich,
 * die Beschriftung bleibt stehen. Breite der Spalte, Zellen und Zeilenhöhe kommen zentral von hier,
 * damit Kopf und Zeilen sauber fluchten; am Handy schmaler und je nach Dichte 3, 7 oder 14 Tage.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type { GuestAccess } from './bookingDetailsModel';
import { DndContext, pointerWithin } from '@dnd-kit/core';
import type { CalendarPropertyRow as RowData } from '@/shared/api';
import { useElementWidth } from '@/shared/hooks/useElementWidth';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { CalendarHeader } from './CalendarHeader';
import type { BookingSpan } from './calendarHelpers';
import { CalendarPropertyRow, type RowGeometry } from './CalendarPropertyRow';
import type { CalendarReassignmentRequest } from './calendarReassignment';
import { FreeDayPrompt, type FreeDayTarget } from './FreeDayPrompt';
import { useCalendarDrag } from './useCalendarDrag';
import type { CalendarRows } from './useCalendarRows';

interface PropertyCalendarGridProps {
  rows: CalendarRows;
  /** Alle Zeilen der Antwort – das Ziehen prüft auch gegen ausgefilterte Ziele. */
  allRows: RowData[];
  addressByPropertyId: Map<string, string>;
  onFreeCellClick?: (propertyId: string, date: string, propertyName: string) => void;
  /** Gastdaten wie der Server: Name und Telefon mit Kontakt-Recht, E-Mail mit vollen Gastdaten. */
  guestAccess: GuestAccess;
  operations: boolean;
  mobileDensity: 3 | 7 | 14;
  today: string;
  canReassign: boolean;
  onReassignmentRequest?: (request: CalendarReassignmentRequest) => void;
}

const MOBILE_SIDEBAR = 72;

/**
 * Am Handy füllt die gewählte Zahl Tage genau die gemessene Breite der Karte; der Balken trägt je
 * Dichte den ganzen Text, nur den Nachnamen oder keinen (Board „Belegung · Handy“).
 */
export function geometryFor(
  mobile: boolean,
  density: 3 | 7 | 14,
  width: number | null,
): RowGeometry {
  if (!mobile) {
    return { sidebarWidth: 230, cellMin: 52, rowHeight: 58, compact: false, labels: 'full' };
  }
  const available = width ?? (typeof window === 'undefined' ? 358 : window.innerWidth - 32);
  const cellMin = Math.max(16, Math.floor((available - MOBILE_SIDEBAR) / density));
  const labels = density === 3 ? 'full' : density === 7 ? 'short' : 'none';
  return { sidebarWidth: MOBILE_SIDEBAR, cellMin, rowHeight: 52, compact: true, labels };
}

export function PropertyCalendarGrid(props: PropertyCalendarGridProps) {
  const { rows } = props;
  const mobile = useMediaQuery('(max-width: 767px)');
  const scroller = useElementWidth<HTMLDivElement>();
  const geometry = geometryFor(mobile, props.mobileDensity, scroller.width);
  const scrollNode = useRef<HTMLDivElement | null>(null);
  const todayIndex = rows.dates.indexOf(props.today);
  const firstDate = rows.dates[0];
  // Reicht das Fenster über die Breite (Handy, 3 Tage), beginnt das Bild bei heute (Board); die drei
  // Tage Rückblick bleiben per Wischen da. Bei 7 und 14 Tagen passt alles und der Rückblick steht vorn.
  useLayoutEffect(() => {
    const node = scrollNode.current;
    if (!mobile || !node || todayIndex <= 0) return;
    node.scrollLeft = todayIndex * geometry.cellMin;
  }, [mobile, todayIndex, geometry.cellMin, firstDate]);
  const drag = useCalendarDrag(props.allRows, props.onReassignmentRequest);
  const [freeDay, setFreeDay] = useState<FreeDayTarget | null>(null);
  const chooseTarget = (span: BookingSpan, sourcePropertyId: string) =>
    props.onReassignmentRequest?.({
      bookingId: span.bookingId,
      sourcePropertyId,
      targetPropertyId: '',
      span,
    });
  const onFreeCellClick = props.onFreeCellClick;

  return (
    <DndContext
      sensors={drag.sensors}
      collisionDetection={pointerWithin}
      onDragEnd={drag.finishDrag}
      accessibility={{ announcements: drag.announcements }}
    >
      <div
        ref={(node) => {
          scroller.ref(node);
          scrollNode.current = node;
        }}
        className="w-full max-w-full overflow-x-auto overscroll-x-contain"
        tabIndex={0}
        aria-label="Belegungskalender, horizontal scrollbar"
      >
        <div className="min-w-full">
          <CalendarHeader
            dates={rows.dates}
            sidebarWidth={geometry.sidebarWidth}
            cellMin={geometry.cellMin}
            sidebarLabel={mobile ? '' : 'Apartment · Auslastung'}
            today={props.today}
            compact={mobile}
          />
          {rows.layouts.map(({ row, isRoom, model }) => (
            <CalendarPropertyRow
              key={row.propertyId}
              row={row}
              model={model}
              isRoom={isRoom}
              address={props.addressByPropertyId.get(row.propertyId)}
              geometry={geometry}
              onFreeDay={
                onFreeCellClick
                  ? (target, date, anchor) =>
                      setFreeDay({
                        propertyId: target.propertyId,
                        propertyName: target.propertyName,
                        date,
                        anchor,
                      })
                  : undefined
              }
              guestAccess={props.guestAccess}
              operations={props.operations}
              today={props.today}
              canReassign={props.canReassign}
              onReassignRequested={chooseTarget}
              dropRefusal={(dragged) => drag.refusalFor(row.propertyId, dragged)}
            />
          ))}
        </div>
      </div>
      <FreeDayPrompt
        target={freeDay}
        mobile={mobile}
        onClose={() => setFreeDay(null)}
        onCreate={(target) =>
          onFreeCellClick?.(target.propertyId, target.date, target.propertyName)
        }
      />
    </DndContext>
  );
}
